import { CHANNEL_SEARCH_MIN_QUERY_LENGTH, type ChannelSearchItem } from "./channel-search.shared";

/**
 * SR2's matching, as a pure function over `(items, query)`.
 *
 * Not a hook: it takes data and returns data, so it is testable with no React
 * at all. The caller may pass the labels already lowercased, which is the one
 * per-keystroke cost that grows with the list.
 *
 * ⛔ ORDERING OF `items` IS THE CALLER'S. Only the starts-with/contains split
 * reorders anything here, and each bucket keeps the order it was given.
 */
export interface ChannelSearchMatch {
  item: ChannelSearchItem;
  /** Where the matched run begins in `item.label`, for the bold span. */
  start: number;
  /** How long that run is. The trimmed query's length. */
  length: number;
}

/** Every item's label lowercased once, index-aligned with `items`. */
export function lowercaseLabels(items: readonly ChannelSearchItem[]): string[] {
  return items.map(item => item.label.toLowerCase());
}

export function matchChannelSearchItems(
  items: readonly ChannelSearchItem[],
  query: string,
  /** `lowercaseLabels(items)`, so a keystroke does not lowercase every label. */
  lowercased: readonly string[] = lowercaseLabels(items)
): ChannelSearchMatch[] {
  const needle = query.trim().toLowerCase();
  if (needle.length < CHANNEL_SEARCH_MIN_QUERY_LENGTH) {
    return [];
  }

  /* Two buckets rather than a sort: SR2 wants starts-with ahead of
     contains-only, and a comparator would have to re-state "and otherwise keep
     the caller's order", which a stable sort only promises and this gives. */
  const startsWith: ChannelSearchMatch[] = [];
  const contains: ChannelSearchMatch[] = [];

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    /* ⚠ The index comes off the LOWERCASED label and is then used to slice the
       ORIGINAL one. That holds because lower-casing is one code unit for one
       code unit in every script this feed carries; a locale where it is not
       (Turkish dotted capital I) would shift the bold span by a character and
       nothing else. */
    const start = lowercased[index].indexOf(needle);
    if (start < 0) {
      continue;
    }
    (start === 0 ? startsWith : contains).push({
      item,
      start,
      length: needle.length
    });
  }

  return [...startsWith, ...contains];
}
